/**
 * Intent v1 — type surface.
 *
 * An `intent.md` is the artifact a reporter (or an agent drafting from an existing
 * FogBugz case) produces: YAML-style frontmatter plus the five fixed markdown
 * sections from Anthropic's AI-native SDLC playbook, with the FogBugz intake rubric
 * and the Epic Request fields nested beneath them as *slots*.
 *
 * This module is deliberately dependency-free and framework-free so the API, the MCP
 * server, and the browser bundle can all consume the same definition.
 */
/** The only schema version this module understands. */
export const INTENT_SCHEMA_VERSION = "v1";
/**
 * Where an intent came from. `agent-draft` is the evidence-only draft produced by
 * `/spectree:bug-to-er --draft-intent`; it is never treated as complete.
 * `fogbugz-toolbox-formulator` is the SpecTree Toolbox FogBugz Formulator (epic
 * PI-SPEC-SW-E309), whose interview runs on the person's own Claude Code with the
 * product code open; the website Formulator keeps `fogbugz-web-formulator`.
 */
export const INTENT_SOURCES = [
    "fogbugz-web-formulator",
    "fogbugz-cli-formulator",
    "fogbugz-toolbox-formulator",
    "agent-draft",
    "manual",
];
/**
 * What the reporter is filing. The kind is an intent-domain concept: it decides which
 * slots the reporter owes and which headings the document carries. It is deliberately
 * *not* a FogBugz category — the `sCategory` → kind mapping lives at the API boundary,
 * so this module stays free of any FogBugz knowledge.
 *
 * `bug` is the fallback for an absent or unrecognised category, because it reproduces
 * the behaviour every document written before the kind existed already had.
 */
export const INTENT_KINDS = ["bug", "feature", "inquiry"];
/**
 * The five playbook headings, in the order they appear in the document. Slot order
 * and section order are both driven from here so the parser and renderer cannot drift.
 */
export const PLAYBOOK_SECTIONS = [
    "problem",
    "proposedOutcome",
    "affectedUsersAndSystems",
    "constraints",
    "openQuestions",
];
/** Human-facing level-2 heading text for each playbook section. */
export const PLAYBOOK_SECTION_TITLES = {
    problem: "Problem",
    proposedOutcome: "Proposed outcome",
    affectedUsersAndSystems: "Affected users and systems",
    constraints: "Constraints",
    openQuestions: "Open questions",
};
/**
 * Every slot id that exists, in playbook order. This is a *superset across all
 * {@link INTENT_KINDS}*, not the set any one reporter owes: which of these a given
 * kind requires is `KIND_SLOT_SETS` in `slots.ts`.
 *
 * The registry in `slots.ts` is typed as a total record over this tuple, so adding an
 * id here without a definition is a type error. Existing ids are never removed or
 * reordered relative to one another — this order drives document section order, and
 * the bug path's document must stay byte-identical.
 */
export const SLOT_IDS = [
    // Problem
    "title",
    "symptom",
    "reproSteps",
    "expectedVsActual",
    "question",
    "questionContext",
    // Proposed outcome
    "desiredOutcome",
    "proposedDirection",
    "successSignal",
    // Affected users and systems
    "usersAffected",
    "frequency",
    "environment",
    "whoBenefits",
    "answerAudience",
    // Constraints
    "severity",
    "reproducibility",
    "workaround",
    "currentWorkaround",
    "answerByWhen",
    "boundary",
];
//# sourceMappingURL=types.js.map